import gauge from '../shared/classic-gauge.json';
import { assetUrl } from './hd-assets';
import { sourceFrameBlend } from './source-frame-blend';

/** Physical canvas density only; source registration stays in Flash pixels. */
export const GAUGE_PIXEL_DENSITY = 4;

/** Original GraphicsUtils.drawSector point order, in logical Flash pixels. */
export function angleSector(min: number, max: number, face: number): string {
  const points = ['51px 52px'];
  for (let a = min; a <= max + 1; a++) {
    const radians = a * Math.PI / 180;
    points.push(`${51 + 55 * Math.cos(radians) * face}px ${52 - 55 * Math.sin(radians)}px`);
  }
  return `polygon(${points.join(',')})`;
}

export function angerSector(fraction: number): string {
  const points = ['52px 52px'];
  const end = 90 - 270 * Math.max(0, Math.min(1, fraction));
  for (let a = 90; a > end; a--) points.push(`${52 + 52 * Math.cos(a * Math.PI / 180)}px ${52 + 52 * Math.sin(a * Math.PI / 180)}px`);
  points.push(`${52 + 52 * Math.cos(end * Math.PI / 180)}px ${52 + 52 * Math.sin(end * Math.PI / 180)}px`);
  return `polygon(${points.join(',')})`;
}

export function angleGauge() {
  const layers = gauge.angle.layersBackToFront.map(layer => {
    const asset = gauge.assets[layer.asset as keyof typeof gauge.assets];
    if (layer.asset === 'angleCircle' || layer.asset === 'angleGreen') return `<div id="${layer.asset === 'angleCircle' ? 'angle-sector' : 'angle-green'}" class="angle-sector ${layer.asset === 'angleGreen' ? 'hidden' : ''}" style="background-image:url('${assetUrl(asset.path)}')"></div>`;
    const id = ({ angleNeedle: 'angle-needle', angleClone: 'angle-clone', angleOverlap: 'angle-overlap' } as Record<string, string>)[layer.asset];
    const hidden = 'initiallyVisible' in layer && !layer.initiallyVisible;
    return `<img ${id ? `id="${id}"` : ''} class="angle-layer ${hidden ? 'hidden' : ''}" src="${assetUrl(asset.path)}" alt="" style="left:${layer.x}px;top:${layer.y}px;width:${layer.width}px;height:${layer.height}px">`;
  });
  // ArrowBg leaves its back/dial upright; only ArrowSub follows the ground.
  return `<div class="angle-gauge"><div class="angle-layer-stack">${layers[0]}<div id="angle-sub">${layers.slice(1, -1).join('')}</div>${layers.at(-1)}<strong id="angle-value">45<small>°</small></strong></div></div>`;
}

export interface RecordedAim { angle: number; face: number; bodyAngle: number }
export function gaugeAim(angle: number, face: number, bodyAngle: number, previous: RecordedAim | null) {
  const needle = face === 1 ? -angle : 180 + angle;
  const clone = previous ? previous.bodyAngle + (previous.face === 1 ? -previous.angle : 180 + previous.angle) - bodyAngle : null;
  return { value: Math.trunc(angle - bodyAngle * face), needle, clone,
    overlap: clone !== null && Math.abs(((needle - clone) % 360 + 540) % 360 - 180) < .001 };
}

export function danderGauge() {
  const ready = gauge.assets.danderReady, label = gauge.assets.danderPow, hit = gauge.dander.button;
  // Exported movie bounds include negative Flash coordinates. Keep its source
  // registration point at (34,33), scaled once, in the same origin as the ring.
  const readyStyle = `left:${ready.placement.x - ready.anchor.x * ready.placement.scale}px;top:${ready.placement.y - ready.anchor.y * ready.placement.scale}px;width:${ready.width * ready.placement.scale}px;height:${ready.height * ready.placement.scale}px`;
  const labelStyle = `left:${label.style.x}px;top:${label.style.y}px;width:${label.style.width}px;height:${label.style.height}px`;
  const hitStyle = `left:${hit.x - hit.hitRadius}px;top:${hit.y - hit.hitRadius}px;width:${hit.hitRadius * 2}px;height:${hit.hitRadius * 2}px`;
  return `<div class="anger-gauge"><div id="anger-fill"></div><img class="pow-label" style="${labelStyle}" src="${assetUrl(label.path)}" alt="必杀技"><canvas id="pow-ready" class="pow-ready hidden" style="${readyStyle}" width="${ready.width * GAUGE_PIXEL_DENSITY}" height="${ready.height * GAUGE_PIXEL_DENSITY}" data-logical-width="${ready.width}" data-logical-height="${ready.height}" aria-hidden="true"></canvas><button id="pow" class="pow-button" style="${hitStyle}" title="怒气满 200 可使用必杀 [B]" aria-label="使用必杀技"></button><span id="anger-text"></span></div>`;
}

/** ToolStripView keeps POW and both native strips in one registration space. */
export function battleStatusStrip() {
  return `<div class="battle-status-strip">${danderGauge()}<div class="status-gauges"><div class="status-meter energy"><i id="energy-fill" data-fill-width="108"></i><span id="energy-text">体力</span></div><div class="status-meter hp"><i id="hp-fill" data-fill-width="122"></i><span id="hp-text">生命</span></div></div></div>`;
}

export const danderReadyFrames = gauge.assets.danderReady.frames;

/** The 360ms source glow retains its nine frames, with visual-only resampling. */
export function danderReadySample(elapsedMs: number) {
  const asset = gauge.assets.danderReady;
  const sample = sourceFrameBlend(elapsedMs, asset.frameRate, asset.frames.length, true);
  return { ...sample, path: asset.frames[sample.frameIndex], nextPath: asset.frames[sample.nextFrameIndex] };
}
